import { t } from "@lingui/macro";
import { MagnifyingGlass } from "@phosphor-icons/react";
import * as PhosphorIcons from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@stylingcv/ui";
import { startTransition, useEffect, useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// Build the icon catalogue once at module load — filter to actual icon
// components (PascalCase functions, excluding IconContext etc.)
// ---------------------------------------------------------------------------
function pascalToKebab(str: string): string {
  return str.replace(/([A-Z])/g, (m, letter, offset) =>
    offset === 0 ? letter.toLowerCase() : "-" + letter.toLowerCase(),
  );
}

const FORWARD_REF = Symbol.for("react.forward_ref");
const MEMO = Symbol.for("react.memo");

function isRenderableComponent(val: unknown): boolean {
  if (typeof val === "function") return true;
  if (typeof val === "object" && val !== null) {
    const t = (val as any).$$typeof;
    return t === FORWARD_REF || t === MEMO;
  }
  return false;
}

const EXCLUDED_EXPORTS = new Set(["IconContext", "IconBase", "SSR"]);

const ICON_CATALOGUE: { pascal: string; kebab: string }[] = (
  Object.keys(PhosphorIcons) as string[]
)
  .filter((key) => {
    if (EXCLUDED_EXPORTS.has(key)) return false;
    if (!/^[A-Z]/.test(key)) return false;
    // Each icon is exported twice: AlignLeft + AlignLeftIcon (alias).
    // The CSS class uses the non-Icon name (ph-align-left), so skip the aliases.
    if (key.endsWith("Icon")) return false;
    return isRenderableComponent((PhosphorIcons as Record<string, unknown>)[key]);
  })
  .map((key) => ({ pascal: key, kebab: pascalToKebab(key) }))
  .sort((a, b) => a.kebab.localeCompare(b.kebab));

// Only render this many icons on the first paint — enough to fill the viewport.
// The rest load via requestIdleCallback while the modal is already visible.
const INITIAL_BATCH = 150;

// ---------------------------------------------------------------------------

type Props = {
  open: boolean;
  currentIcon?: string;
  onSelect: (kebabName: string) => void;
  onClose: () => void;
};

export const IconPickerModal = ({ open, currentIcon, onSelect, onClose }: Props) => {
  const [search, setSearch] = useState("");
  const [allLoaded, setAllLoaded] = useState(false);

  // Reset on close; schedule full load after the modal is visually open.
  useEffect(() => {
    if (!open) {
      setAllLoaded(false);
      return;
    }
    const schedule = (window as any).requestIdleCallback ?? ((fn: () => void) => setTimeout(fn, 60));
    const cancel = (window as any).cancelIdleCallback ?? clearTimeout;
    const id = schedule(() => startTransition(() => setAllLoaded(true)), { timeout: 600 });
    return () => cancel(id);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/\s+/g, "-");
    if (!q) return ICON_CATALOGUE;
    return ICON_CATALOGUE.filter(({ kebab }) => kebab.includes(q));
  }, [search]);

  // When the list is small (e.g. after searching) always show all results.
  const visibleIcons = (allLoaded || filtered.length <= INITIAL_BATCH)
    ? filtered
    : filtered.slice(0, INITIAL_BATCH);

  const handleSelect = (kebab: string) => {
    onSelect(kebab);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent
        style={{
          maxWidth: 560,
          width: "calc(100vw - 32px)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          overflow: "hidden",
          gap: 0,
        }}
      >
        {/* Header */}
        <DialogHeader
          style={{ padding: "16px 20px 0", flexShrink: 0 }}
          bordered={false}
        >
          <DialogTitle style={{ fontSize: 15, fontWeight: 700 }}>
            {t`Choose Icon`}
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div
          style={{
            padding: "12px 20px",
            flexShrink: 0,
            borderBottom: "1px solid var(--border,#e5e5e5)",
          }}
        >
          <div style={{ position: "relative" }}>
            <MagnifyingGlass
              size={15}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--fg-4,#a3a3a3)",
                pointerEvents: "none",
              }}
            />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t`Search icons…`}
              style={{
                width: "100%",
                height: 36,
                paddingLeft: 32,
                paddingRight: 10,
                border: "1px solid var(--border,#e5e5e5)",
                borderRadius: 8,
                fontSize: 13,
                color: "var(--fg-1,#171717)",
                background: "#fafafa",
                outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "var(--accent,#268599)";
                e.currentTarget.style.background = "#fff";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "var(--border,#e5e5e5)";
                e.currentTarget.style.background = "#fafafa";
              }}
            />
          </div>
          <p
            style={{
              marginTop: 6,
              fontSize: 10,
              color: "var(--fg-4,#a3a3a3)",
              lineHeight: 1.4,
            }}
          >
            {filtered.length === ICON_CATALOGUE.length
              ? `${ICON_CATALOGUE.length} icons`
              : `${filtered.length} of ${ICON_CATALOGUE.length} icons`}
          </p>
        </div>

        {/* Grid */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 16px 16px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
            gap: 4,
            alignContent: "start",
          }}
        >
          {/* None option — always first */}
          {(() => {
            const isSelected = !currentIcon;
            return (
              <button
                key="__none__"
                title="none"
                onClick={() => handleSelect("")}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  padding: "8px 4px 6px",
                  borderRadius: 8,
                  border: isSelected
                    ? "2px solid var(--accent,#268599)"
                    : "1px solid transparent",
                  background: isSelected
                    ? "rgba(38,133,153,0.08)"
                    : "transparent",
                  cursor: "pointer",
                  transition: "background 0.1s, border-color 0.1s",
                  minHeight: 64,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.background = "#f5f5f5";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--border,#e5e5e5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent";
                  }
                }}
              >
                {/* Crossed-out circle to represent "no icon" */}
                <svg
                  width={22}
                  height={22}
                  viewBox="0 0 22 22"
                  fill="none"
                  style={{ color: isSelected ? "var(--accent,#268599)" : "var(--fg-2,#404040)", flexShrink: 0 }}
                >
                  <circle cx="11" cy="11" r="9" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="4.5" y1="4.5" x2="17.5" y2="17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span
                  style={{
                    fontSize: 8,
                    lineHeight: 1.3,
                    textAlign: "center",
                    color: isSelected ? "var(--accent,#268599)" : "var(--fg-4,#a3a3a3)",
                    wordBreak: "break-all",
                    fontWeight: isSelected ? 600 : 400,
                    maxWidth: 64,
                  }}
                >
                  none
                </span>
              </button>
            );
          })()}

          {visibleIcons.map(({ pascal, kebab }) => {
            const Icon = (PhosphorIcons as Record<string, any>)[pascal];
            const isSelected = currentIcon === kebab;
            return (
              <button
                key={pascal}
                title={kebab}
                onClick={() => handleSelect(kebab)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                  padding: "8px 4px 6px",
                  borderRadius: 8,
                  border: isSelected
                    ? "2px solid var(--accent,#268599)"
                    : "1px solid transparent",
                  background: isSelected
                    ? "rgba(38,133,153,0.08)"
                    : "transparent",
                  cursor: "pointer",
                  transition: "background 0.1s, border-color 0.1s",
                  minHeight: 64,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.background = "#f5f5f5";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--border,#e5e5e5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                    (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent";
                  }
                }}
              >
                <Icon
                  size={22}
                  weight={isSelected ? "duotone" : "regular"}
                  style={{
                    color: isSelected ? "var(--accent,#268599)" : "var(--fg-2,#404040)",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 8,
                    lineHeight: 1.3,
                    textAlign: "center",
                    color: isSelected ? "var(--accent,#268599)" : "var(--fg-4,#a3a3a3)",
                    wordBreak: "break-all",
                    fontWeight: isSelected ? 600 : 400,
                    maxWidth: 64,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {kebab}
                </span>
              </button>
            );
          })}

          {filtered.length === 0 && (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: "40px 0",
                textAlign: "center",
                color: "var(--fg-4,#a3a3a3)",
                fontSize: 13,
              }}
            >
              {t`No icons match`} &ldquo;{search}&rdquo;
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
